import _ from "lodash";
import {
  DEFAULT_COLUMNS_ROWS_KEY,
  ACROSS_YEAR_AGGREGATES_KEY,
  GRADING_PERIODS_SETTINGS_KEY,
  GRADE_OPTION_KEY_PREFIX,
  GRADE_WISE_NODE_KEY,
} from "../../../modules/constants";

export const getGradeWiseOptions = ({ academicRecordsData }) => {
  const gradeMappings = _.sortBy(
    _.get(academicRecordsData, "node.transcriptSummary.grades", []),
    "displaySequence"
  );

  return _.map(gradeMappings, gradeMap => {
    const isOverridden = _.get(gradeMap, "configuration.isOverridden", false);
    const gradeName = _.get(gradeMap, "grade.name");

    return {
      key: `${GRADE_OPTION_KEY_PREFIX}${gradeMap.id}`,
      title: gradeName,
      isOverridden,
      parent: GRADE_WISE_NODE_KEY,
      tier: 1,
      children: [],
    };
  });
};

export const getSelectableKeys = ({ gradeWiseOptions }) =>
  new Set([
    DEFAULT_COLUMNS_ROWS_KEY,
    ACROSS_YEAR_AGGREGATES_KEY,
    GRADING_PERIODS_SETTINGS_KEY,
    ..._.map(gradeWiseOptions, "key"),
  ]);

export const getInitialSelectedTab = ({ metadataSelectedTab }) => {
  if (!metadataSelectedTab) return GRADING_PERIODS_SETTINGS_KEY;
  if (
    _.includes(
      [
        DEFAULT_COLUMNS_ROWS_KEY,
        ACROSS_YEAR_AGGREGATES_KEY,
        GRADING_PERIODS_SETTINGS_KEY,
      ],
      metadataSelectedTab
    )
  ) {
    return metadataSelectedTab;
  }
  return `${GRADE_OPTION_KEY_PREFIX}${metadataSelectedTab}`;
};

export const getSelectedGradeId = ({ selectedTab }) => {
  if (!_.startsWith(selectedTab, GRADE_OPTION_KEY_PREFIX)) {
    return null;
  }
  return _.replace(selectedTab, GRADE_OPTION_KEY_PREFIX, "");
};

export const getSelectedDraftKey = ({ selectedGradeId }) =>
  selectedGradeId || "DEFAULT";

export const getDraftConfiguration = ({ updates, selectedGradeId }) => {
  const draftKey = selectedGradeId || "DEFAULT";
  return _.get(updates, [draftKey, "configuration"], null);
};

export const getDefaultPeriodTemplate = gradeScalesConfig => {
  const periodWise = _.get(gradeScalesConfig, "gradingPeriodWise", {});
  return (
    _.head(_.values(periodWise)) ||
    _.get(gradeScalesConfig, "acrossGradingPeriods", null) ||
    (_.get(gradeScalesConfig, "displayOrder")
      ? {
          displayOrder: _.get(gradeScalesConfig, "displayOrder", []),
          config: _.get(gradeScalesConfig, "config", {}),
        }
      : null)
  );
};

export const mapGradingPeriodsFromBackend = gradingPeriods =>
  _.map(gradingPeriods, (gp, index) => ({
    gradingPeriodId: String(_.get(gp, "gradingPeriod.id")),
    displaySequence: _.get(gp, "displaySequence", index + 1),
    isEnabled: _.get(gp, "isEnabled", true),
  }));

export const mapDraftPeriodsToBackendShape = draftPeriods =>
  _.map(draftPeriods, p => ({
    gradingPeriod: { id: p.gradingPeriodId },
    displaySequence: p.displaySequence,
    isEnabled: p.isEnabled,
  }));

const getCurrentGradeMapPeriods = ({ gradeMap, gradingPeriodDrafts }) => {
  const gradeMapId = _.get(gradeMap, "id");
  const draftPeriods = _.get(gradingPeriodDrafts, gradeMapId);

  if (draftPeriods) {
    return draftPeriods;
  }

  return mapGradingPeriodsFromBackend(_.get(gradeMap, "gradingPeriods", []));
};

export const filterGradeScaleConfig = ({
  periodConfig,
  selectedGradeMap,
  orgGradeScalesData,
  curriculumProgramsData,
}) => {
  if (!periodConfig || !selectedGradeMap) return periodConfig;

  const displayOrder = _.get(periodConfig, "displayOrder", []);
  const config = _.get(periodConfig, "config", {});
  if (_.isEmpty(displayOrder)) return periodConfig;

  const selectedGradeSchoolId = _.get(selectedGradeMap, "school.sourceId");

  const programIdBySourceGradeScaleId = _.reduce(
    curriculumProgramsData,
    (acc, program) => {
      const programId = _.get(program, "id");
      _.forEach(_.get(program, "gradeScale.edges", []), edge => {
        const gradeScaleId = _.get(edge, "node.id");
        if (programId && gradeScaleId) acc[gradeScaleId] = programId;
      });
      return acc;
    },
    {}
  );

  const gradeScaleMetadataById = _.reduce(
    orgGradeScalesData,
    (acc, gradeScale) => {
      const transcriptId = _.get(gradeScale, "id");
      const sourceId = _.get(gradeScale, "source.id");
      const isCustom = _.get(gradeScale, "isCustom", false);
      if (transcriptId) acc[transcriptId] = { sourceId, isCustom };
      return acc;
    },
    {}
  );

  const shouldInclude = gradeScaleId => {
    const metadata = gradeScaleMetadataById[gradeScaleId];
    if (!metadata) return true;
    if (metadata.isCustom) return true;
    if (selectedGradeSchoolId && metadata.sourceId) {
      return (
        _.get(programIdBySourceGradeScaleId, metadata.sourceId) ===
        selectedGradeSchoolId
      );
    }
    return false;
  };

  return {
    displayOrder: _.filter(displayOrder, shouldInclude),
    config: _.reduce(
      config,
      (acc, value, key) => {
        if (shouldInclude(key)) acc[key] = value;
        return acc;
      },
      {}
    ),
  };
};

const syncGradeScalesWithAllPeriods = ({
  gradeScales = {},
  allPeriods = [],
  backendGradeScales = {},
  defaultGradeScales = {},
  selectedGradeMap,
  orgGradeScalesData,
  curriculumProgramsData,
}) => {
  if (_.isEmpty(gradeScales)) {
    return gradeScales;
  }

  const existingPeriodWise = _.get(gradeScales, "gradingPeriodWise", {});
  const backendPeriodWise = _.get(backendGradeScales, "gradingPeriodWise", {});
  const defaultPeriodTemplate = getDefaultPeriodTemplate(defaultGradeScales);

  const nextPeriodWise = _.reduce(
    allPeriods,
    (acc, period) => {
      const gradingPeriodId = _.toString(_.get(period, "gradingPeriodId"));
      if (!gradingPeriodId) {
        return acc;
      }

      // 1. Own config (current session edits or draft)
      // 2. Own config (previously saved to backend)
      const ownConfig =
        existingPeriodWise[gradingPeriodId] ||
        backendPeriodWise[gradingPeriodId];

      if (ownConfig) {
        acc[gradingPeriodId] = _.cloneDeep(ownConfig);
      } else {
        // 3. New GP → copy from Default, filtered by school
        acc[gradingPeriodId] = _.cloneDeep(
          filterGradeScaleConfig({
            periodConfig: defaultPeriodTemplate,
            selectedGradeMap,
            orgGradeScalesData,
            curriculumProgramsData,
          }) || {}
        );
      }
      return acc;
    },
    {}
  );

  return {
    ...gradeScales,
    gradingPeriodWise: nextPeriodWise,
  };
};

const buildInheritedGradeConfiguration = ({
  defaultConfiguration,
  selectedGradeMap,
  gradingPeriods,
  orgGradeScalesData,
  curriculumProgramsData,
}) => {
  if (_.isEmpty(defaultConfiguration)) {
    return null;
  }

  return {
    general: _.cloneDeep(defaultConfiguration.general),
    columnAggregates: _.cloneDeep(defaultConfiguration.columnAggregates),
    rowAggregates: _.cloneDeep(defaultConfiguration.rowAggregates),
    yearAggregates: _.cloneDeep(defaultConfiguration.yearAggregates),
    gradeScales: buildGradeScalesForGradeLevelTranscriptConfiguration({
      gradingPeriods,
      defaultConfiguration,
      selectedGradeMap,
      orgGradeScalesData,
      curriculumProgramsData,
    }),
  };
};

// Build the configuration body to send for a grade whose dot is on.
// Caller has already decided the grade needs a config payload; this just
// constructs the shape (draft preferred, otherwise inherit from default /
// backend) and syncs gradeScales with the current period list.
const getGradeConfigurationForPayload = ({
  gradeMap,
  gradeUpdate,
  defaultConfiguration,
  gradingPeriodDrafts,
  orgGradeScalesData,
  curriculumProgramsData,
}) => {
  const backendConfiguration = _.get(gradeMap, "configuration", null);
  const currentPeriods = getCurrentGradeMapPeriods({
    gradeMap,
    gradingPeriodDrafts,
  });
  const enabledPeriods = _.filter(currentPeriods, { isEnabled: true });
  const draftConfiguration = _.get(gradeUpdate, "configuration", null);
  const backendIsOverridden = _.get(
    backendConfiguration,
    "isOverridden",
    false
  );

  let baseConfiguration = draftConfiguration;

  if (!baseConfiguration) {
    baseConfiguration = backendIsOverridden
      ? _.cloneDeep(backendConfiguration)
      : buildInheritedGradeConfiguration({
          defaultConfiguration,
          selectedGradeMap: gradeMap,
          gradingPeriods: _.map(enabledPeriods, period => ({
            gradingPeriod: {
              id: period.gradingPeriodId,
            },
            displaySequence: period.displaySequence,
          })),
          orgGradeScalesData,
          curriculumProgramsData,
        });
  }

  if (!baseConfiguration) {
    return null;
  }

  return {
    ...baseConfiguration,
    gradeScales: syncGradeScalesWithAllPeriods({
      gradeScales: _.get(baseConfiguration, "gradeScales", {}),
      allPeriods: currentPeriods,
      backendGradeScales: _.get(backendConfiguration, "gradeScales", {}),
      defaultGradeScales: _.get(defaultConfiguration, "gradeScales", {}),
      selectedGradeMap: gradeMap,
      orgGradeScalesData,
      curriculumProgramsData,
    }),
  };
};

export const getConfigurationPayload = ({
  academicRecordsData,
  updates,
  gradingPeriodDrafts = {},
  orgGradeScalesData = [],
  curriculumProgramsData = [],
}) => {
  const defaultUpdate = _.get(updates, "DEFAULT", {});
  const isGeneralDirty = _.get(defaultUpdate, "isDirty", false);
  const defaultConfiguration = _.get(defaultUpdate, "configuration", null);
  const originalDefaultConfiguration = _.get(
    academicRecordsData,
    "node.transcriptSummary.configuration",
    null
  );
  const isDefaultOriginallyOverridden = _.get(
    originalDefaultConfiguration,
    "isOverridden",
    false
  );

  const gradeMappings = _.get(
    academicRecordsData,
    "node.transcriptSummary.grades",
    []
  );

  const gradePayload = _.compact(
    _.map(gradeMappings, gradeMap => {
      const studentGradeMapId = _.get(gradeMap, "id");
      const gradeId = _.get(gradeMap, "grade.id");
      const gradeUpdate = _.get(updates, studentGradeMapId, {});
      const isDirty = _.get(gradeUpdate, "isDirty", false);
      const isResetToDefault = _.get(gradeUpdate, "isResetToDefault", false);

      const periods = _.has(gradingPeriodDrafts, studentGradeMapId)
        ? _.get(gradingPeriodDrafts, studentGradeMapId, [])
        : _.get(gradeMap, "gradingPeriods", []);
      const hasAnyDisabledGradingPeriod = _.some(
        periods,
        p => _.get(p, "isEnabled") === false
      );

      if (!isDirty && !isResetToDefault && !hasAnyDisabledGradingPeriod) {
        return null;
      }

      if (isResetToDefault && !hasAnyDisabledGradingPeriod) {
        return {
          entityType: "GRADE",
          entityId: gradeId,
          configuration: null,
        };
      }

      const configuration = getGradeConfigurationForPayload({
        gradeMap,
        gradeUpdate,
        defaultConfiguration:
          defaultConfiguration || originalDefaultConfiguration,
        gradingPeriodDrafts,
        orgGradeScalesData,
        curriculumProgramsData,
      });

      if (!configuration) {
        return null;
      }

      return {
        entityType: "GRADE",
        entityId: gradeId,
        configuration: {
          ...configuration,
          isOverridden: true,
        },
      };
    })
  );

  let studentPayload = null;

  if (isDefaultOriginallyOverridden) {
    studentPayload = {
      entityType: "STUDENT",
      entityId: null,
      configuration: originalDefaultConfiguration,
    };
  }

  if (isGeneralDirty) {
    studentPayload = {
      entityType: "STUDENT",
      entityId: null,
      configuration: {
        ...defaultConfiguration,
        isOverridden: true,
      },
    };
  }

  return _.compact([studentPayload, ...gradePayload]);
};

export const getBulkConfigurationPayload = ({
  academicRecordsData,
  updates,
}) => {
  const defaultUpdate = _.get(updates, "DEFAULT", {});
  const isGeneralDirty = _.get(defaultUpdate, "isDirty", false);
  const defaultConfiguration = _.get(defaultUpdate, "configuration", null);
  const originalDefaultConfiguration = _.get(
    academicRecordsData,
    "node.transcriptSummary.configuration",
    null
  );
  const isDefaultOriginallyOverridden = _.get(
    originalDefaultConfiguration,
    "isOverridden",
    false
  );

  const gradeMappings = _.get(
    academicRecordsData,
    "node.transcriptSummary.grades",
    []
  );

  const gradePayload = _.map(gradeMappings, gradeMap => {
    const studentGradeMapId = _.get(gradeMap, "id");
    const gradeId = _.get(gradeMap, "grade.id");
    const gradeUpdate = _.get(updates, studentGradeMapId, {});
    const isDirty = _.get(gradeUpdate, "isDirty", false);
    const isResetToDefault = _.get(gradeUpdate, "isResetToDefault", false);
    const originalConfiguration = _.get(gradeMap, "configuration", {});
    const isOriginallyOverridden = _.get(
      originalConfiguration,
      "isOverridden",
      false
    );

    if (isResetToDefault) {
      return {
        entityType: "GRADE",
        entityId: gradeId,
        configuration: null,
      };
    }

    if (isDirty) {
      return {
        entityType: "GRADE",
        entityId: gradeId,
        configuration: {
          ..._.get(gradeUpdate, "configuration", {}),
          isOverridden: true,
        },
      };
    }

    if (isOriginallyOverridden) {
      return {
        entityType: "GRADE",
        entityId: gradeId,
        configuration: originalConfiguration,
      };
    }

    return {
      entityType: "GRADE",
      entityId: gradeId,
      configuration: null,
    };
  });

  let studentPayload = {
    entityType: "STUDENT",
    entityId: null,
    configuration: null,
  };

  if (isDefaultOriginallyOverridden) {
    studentPayload = {
      entityType: "STUDENT",
      entityId: null,
      configuration: originalDefaultConfiguration,
    };
  }

  if (isGeneralDirty) {
    studentPayload = {
      entityType: "STUDENT",
      entityId: null,
      configuration: {
        ...defaultConfiguration,
        isOverridden: true,
      },
    };
  }

  return _.compact([studentPayload, ...gradePayload]);
};

export function buildGradeScalesForGradeLevelTranscriptConfiguration(data) {
  const {
    gradingPeriods,
    defaultConfiguration = {},
    selectedGradeMap,
    orgGradeScalesData = [],
    curriculumProgramsData = [],
  } = data || {};

  const defaultGradeScaleDisplayOrder = _.cloneDeep(
    _.get(defaultConfiguration, "gradeScales.displayOrder", [])
  );
  const defaultGradeScaleConfig = _.cloneDeep(
    _.get(defaultConfiguration, "gradeScales.config", {})
  );

  // Get the curriculum program/school ID for the selected grade
  const selectedGradeSchoolId = _.get(selectedGradeMap, "school.sourceId");

  // Build mapping of source grade scale ID to curriculum program ID
  const programIdBySourceGradeScaleId = _.reduce(
    curriculumProgramsData,
    (acc, program) => {
      const programId = _.get(program, "id");
      _.forEach(_.get(program, "gradeScale.edges", []), edge => {
        const gradeScaleId = _.get(edge, "node.id");
        if (programId && gradeScaleId) {
          acc[gradeScaleId] = programId;
        }
      });
      return acc;
    },
    {}
  );

  // Build mapping of transcript grade scale ID to source grade scale ID and custom status
  const gradeScaleMetadataById = _.reduce(
    orgGradeScalesData,
    (acc, gradeScale) => {
      const transcriptId = _.get(gradeScale, "id");
      const sourceId = _.get(gradeScale, "source.id");
      const isCustom = _.get(gradeScale, "isCustom", false);

      if (transcriptId) {
        acc[transcriptId] = {
          sourceId,
          isCustom,
        };
      }
      return acc;
    },
    {}
  );

  /**
   * Filter function to determine if a grade scale should be included
   * for the selected grade
   */
  const shouldIncludeGradeScale = gradeScaleId => {
    const metadata = _.get(gradeScaleMetadataById, gradeScaleId);

    // 1. If metadata not found, it's a PREDEFINED column (CREDIT, FINAL_SCORE, etc.)
    //    These are system-level and not in orgGradeScalesData - ALWAYS include
    if (!metadata) {
      return true;
    }

    // 2. Always include CUSTOM grade scales (organization-wide)
    if (metadata.isCustom) {
      return true;
    }

    // 3. For SCHOOL-RELATED grade scales, only include if they belong
    //    to the same school/curriculum program as the selected grade
    if (selectedGradeSchoolId && metadata.sourceId) {
      const gradeScaleProgramId = _.get(
        programIdBySourceGradeScaleId,
        metadata.sourceId
      );
      return gradeScaleProgramId === selectedGradeSchoolId;
    }

    // If school-related but no school association, exclude
    return false;
  };

  // Filter display order to only include applicable grade scales
  const filteredDisplayOrder = _.filter(
    defaultGradeScaleDisplayOrder,
    gradeScaleId => shouldIncludeGradeScale(gradeScaleId)
  );

  // Filter config to only include applicable grade scales
  const filteredConfig = _.reduce(
    defaultGradeScaleConfig,
    (acc, value, key) => {
      if (shouldIncludeGradeScale(key)) {
        acc[key] = _.cloneDeep(value);
      }
      return acc;
    },
    {}
  );

  const updatedGradingPeriodWise = _.reduce(
    gradingPeriods || [],
    (acc, gradingPeriodData) => {
      const gradingPeriodId =
        _.get(gradingPeriodData, "fk_grading_period_id") ||
        _.get(gradingPeriodData, "gradingPeriod.id") ||
        _.get(gradingPeriodData, "id");
      if (!gradingPeriodId) {
        return acc;
      }

      acc[gradingPeriodId] = {
        displayOrder: _.cloneDeep(filteredDisplayOrder),
        config: _.cloneDeep(filteredConfig),
      };
      return acc;
    },
    {}
  );

  const updatedAcrossGradingPeriods = {
    displayOrder: _.cloneDeep(filteredDisplayOrder),
    config: _.cloneDeep(filteredConfig),
  };

  return {
    viewType: "GRADING_PERIOD_WISE",
    gradingPeriodWise: updatedGradingPeriodWise,
    acrossGradingPeriods: updatedAcrossGradingPeriods,
  };
}

export const getResetDraftConfiguration = ({
  academicRecordsData,
  selectedGradeId,
  defaultDraftConfiguration,
  orgGradeScalesData,
  curriculumProgramsData,
  effectivePeriods,
}) => {
  const selectedGradeMap = _.find(
    _.get(academicRecordsData, "node.transcriptSummary.grades", []),
    gradeMap => _.get(gradeMap, "id") === selectedGradeId
  );
  const defaultConfiguration =
    defaultDraftConfiguration ||
    _.get(academicRecordsData, "node.transcriptSummary.configuration", {});

  if (_.isEmpty(defaultConfiguration)) {
    return null;
  }

  return buildInheritedGradeConfiguration({
    defaultConfiguration,
    selectedGradeMap,
    gradingPeriods:
      effectivePeriods || _.get(selectedGradeMap, "gradingPeriods", []),
    orgGradeScalesData,
    curriculumProgramsData,
  });
};
